import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../config/api';
import { formatDateDisplay } from '../utils/formatDate';
import './PosCakeOrder.css';

const WEIGHT_OPTIONS = Array.from({ length: 60 }, (_, i) => Math.round((i + 1) * 0.5 * 100) / 100); // 0.5 … 30
const ADVANCE_PAYMENT_MODES = ['Cash', 'Card', 'Online', 'Cheque', 'Store-1-QR', 'Store-2-QR'];

function splitWeight(total) {
  const kg = Math.floor(total);
  const dec = Math.round((total - kg) * 100) / 100;
  return { kg, dec };
}

/** Modal: counter / pickup / delivery flows */
export function PosCakeWizard({
  open,
  onClose,
  storeId,
  orderCategory = 'Store-1',
  onAddCounterLine,
  formatInr,
  registerSessionActive,
  serverDate
}) {
  const [phase, setPhase] = useState('pick'); // pick | counter | pickup | delivery
  const [flavours, setFlavours] = useState([]);
  const [flavourId, setFlavourId] = useState('');
  const [flavourName, setFlavourName] = useState('');
  const [weight, setWeight] = useState(1);
  const [price, setPrice] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('12:00');
  const [advance, setAdvance] = useState('');
  const [advanceMode, setAdvanceMode] = useState('Cash');
  const [busy, setBusy] = useState(false);
  const [localMsg, setLocalMsg] = useState(null);

  const reset = useCallback(() => {
    setPhase('pick');
    setFlavourId('');
    setFlavourName('');
    setWeight(1);
    setPrice('');
    setImageFile(null);
    setCustomerName('');
    setCustomerPhone('');
    setAddress('');
    setDeliveryDate(serverDate || new Date().toISOString().slice(0, 10));
    setDeliveryTime('12:00');
    setAdvance('');
    setAdvanceMode('Cash');
    setLocalMsg(null);
  }, [serverDate]);

  useEffect(() => {
    if (!open) return;
    reset();
    (async () => {
      try {
        const { data } = await api.get('/users/flavours');
        setFlavours(data.flavours || []);
      } catch {
        setFlavours([]);
      }
    })();
  }, [open, reset]);

  const lookupPrice = useCallback(async () => {
    const sid = parseInt(storeId, 10);
    if (!sid || !flavourName || weight == null) return;
    try {
      const { data } = await api.get('/pos/cake-prices', {
        params: { storeId: sid, flavour: flavourName, weightKg: weight }
      });
      if (data.price != null) setPrice(String(data.price));
    } catch {
      /* optional matrix */
    }
  }, [storeId, flavourName, weight]);

  useEffect(() => {
    if (!open || phase === 'pick') return;
    lookupPrice();
  }, [flavourName, weight, phase, open, lookupPrice]);

  const onFlavourChange = (e) => {
    const id = e.target.value;
    setFlavourId(id);
    const f = flavours.find((x) => String(x.id) === String(id));
    setFlavourName(f ? f.name : '');
  };

  const handleAddCounter = () => {
    if (!registerSessionActive) {
      setLocalMsg({ type: 'err', text: 'Tap “New order” on the register first.' });
      return;
    }
    const p = parseFloat(price);
    if (!flavourName.trim()) {
      setLocalMsg({ type: 'err', text: 'Choose a flavour.' });
      return;
    }
    if (!Number.isFinite(p) || p < 0) {
      setLocalMsg({ type: 'err', text: 'Enter a valid price (or wait for auto price).' });
      return;
    }
    const desc = `Counter cake · ${flavourName} · ${weight} kg`;
    onAddCounterLine(desc, p);
    onClose();
    reset();
  };

  const printKitchenCopy = (order, item, imageUrl, opts = {}) => {
    const w = window.open('', '_blank', 'width=420,height=720');
    if (!w) return;
    const imgHtml = imageUrl
      ? `<div style="margin:12px 0"><img src="${imageUrl}" alt="Reference" style="max-width:100%;border:1px solid #ccc" /></div>`
      : '';
    const due = opts.due != null ? opts.due : parseFloat(order.due_amount) || 0;
    const adv = opts.advance != null ? opts.advance : parseFloat(order.paid_amount) || 0;
    const total = parseFloat(order.total_amount) || 0;
    const cust = [order.customer_name, order.customer_phone].filter(Boolean).join(' · ');
    const flavour = item?.cake_details?.flavour ?? item?.flavour ?? '—';
    const wkg = item?.cake_details?.weight_kg ?? item?.weight_kg ?? '';
    const wdec = item?.cake_details?.weight_decimal ?? item?.weight_decimal ?? 0;
    const notes = String(
      item?.cake_details?.special_instructions ?? item?.cake_instructions ?? ''
    ).replace(/</g, '');
    w.document.write(`
      <!DOCTYPE html><html><head><title>Kitchen · ${order.order_number || ''}</title></head><body style="font-family:sans-serif;padding:16px;color:#111">
      <h2 style="margin:0 0 8px">Kitchen copy</h2>
      <p><strong>${order.order_number || ''}</strong> · ${order.pos_cake_mode || ''}</p>
      ${cust ? `<p>Customer: ${cust}</p>` : ''}
      <p>${order.pos_cake_mode === 'delivery' ? 'Delivery' : 'Pickup'}: ${opts.dueDate || order.delivery_date || '—'} ${opts.dueTime || order.delivery_time || ''}</p>
      <p>Flavour: <strong>${flavour}</strong></p>
      <p>Weight: ${wkg} kg + ${wdec}</p>
      <p>Total ${total.toFixed(0)} · Advance ${adv.toFixed(0)} · Due ${due.toFixed(0)}</p>
      <p>Notes: ${notes}</p>
      ${imgHtml}
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    w.document.close();
  };

  const submitOrder = async (mode) => {
    const sid = parseInt(storeId, 10);
    if (!sid) {
      setLocalMsg({ type: 'err', text: 'Select an outlet (store) on the register.' });
      return;
    }
    if (!flavourName.trim()) {
      setLocalMsg({ type: 'err', text: 'Choose a flavour.' });
      return;
    }
    if (!customerName.trim() || !customerPhone.trim()) {
      setLocalMsg({ type: 'err', text: 'Customer name and phone are required.' });
      return;
    }
    if (mode === 'delivery' && !address.trim()) {
      setLocalMsg({ type: 'err', text: 'Delivery address is required.' });
      return;
    }
    if ((mode === 'delivery' || mode === 'pickup') && !deliveryDate) {
      setLocalMsg({
        type: 'err',
        text: mode === 'pickup' ? 'Pickup date is required.' : 'Delivery date is required.'
      });
      return;
    }
    if ((mode === 'delivery' || mode === 'pickup') && !deliveryTime) {
      setLocalMsg({
        type: 'err',
        text: mode === 'pickup' ? 'Pickup time is required.' : 'Delivery time is required.'
      });
      return;
    }
    const cakeAmount = parseFloat(price);
    if (!Number.isFinite(cakeAmount) || cakeAmount <= 0) {
      setLocalMsg({ type: 'err', text: 'Cake amount must be greater than 0.' });
      return;
    }
    const adv = parseFloat(advance) || 0;
    const { kg, dec } = splitWeight(Number(weight));
    const delDate = deliveryDate;
    let timeStr = '12:00:00';
    if (deliveryTime) {
      const t = deliveryTime.length <= 5 ? `${deliveryTime}:00` : deliveryTime;
      timeStr = t.slice(0, 8);
    }

    setBusy(true);
    setLocalMsg(null);
    try {
      const { data } = await api.post('/orders', {
        category: orderCategory,
        customer_name: customerName.trim(),
        customer_address: mode === 'delivery' ? address.trim() : 'Pickup at counter',
        customer_phone: customerPhone.trim(),
        delivery_date: delDate,
        delivery_time: timeStr,
        agent_id: null,
        items: [
          {
            item_type: 'Cakes',
            quantity: 1,
            unit_price: cakeAmount,
            total_price: cakeAmount,
            cake_details: {
              product_name: `POS ${mode} cake`,
              flavour: flavourName,
              weight_kg: kg,
              weight_decimal: dec,
              price: cakeAmount,
              message_on_cake: `${mode} · ${flavourName} · ${weight} kg`,
              special_instructions: `POS cake ${mode}. Store ${sid}.`
            }
          }
        ],
        advance_payment: adv,
        advance_payment_mode: adv > 0 ? advanceMode : undefined,
        pos_cake_mode: mode,
        pos_store_id: sid
      });

      const orderId = data.orderId;
      const itemId = data.firstOrderItemId;
      if (imageFile && itemId) {
        const fd = new FormData();
        fd.append('file', imageFile);
        fd.append('fileType', 'cake_image');
        await api.post(`/upload/${itemId}`, fd);
      }

      let imageUrl = null;
      if (imageFile && itemId) {
        try {
          const ordRes = await api.get(`/orders/${orderId}`);
          const files = ordRes.data?.order?.items?.[0]?.files || [];
          const img = files.find((f) => f.file_type === 'cake_image');
          if (img?.file_path) {
            imageUrl = img.file_path.startsWith('http')
              ? img.file_path
              : `${window.location.origin}${img.file_path}`;
          }
        } catch {
          /* ignore */
        }
      }

      const ordRes = await api.get(`/orders/${orderId}`);
      const order = ordRes.data.order;
      const item = order?.items?.[0];

      printKitchenCopy(order, item, imageUrl, {
        advance: adv,
        due: Math.max(0, cakeAmount - adv),
        dueDate: delDate,
        dueTime: timeStr.slice(0, 5)
      });

      setLocalMsg({ type: 'ok', text: `Order ${order.order_number} created.` });
      setTimeout(() => {
        onClose();
        reset();
      }, 800);
    } catch (err) {
      setLocalMsg({
        type: 'err',
        text: err.response?.data?.error || err.response?.data?.message || 'Could not create order'
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="pos-cake-overlay" role="presentation" onClick={() => !busy && onClose()}>
      <div
        className="pos-cake-modal pos-cake-modal-shell"
        role="dialog"
        aria-labelledby="pos-cake-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pos-cake-modal-head">
          <h3 id="pos-cake-title" className="pos-cake-modal-title">
            Cake order
          </h3>
          <button type="button" className="pos-cake-modal-close" onClick={() => !busy && onClose()} aria-label="Close">
            ×
          </button>
        </div>

        {localMsg ? (
          <div className={`pos-cake-msg ${localMsg.type === 'err' ? 'is-err' : 'is-ok'}`}>{localMsg.text}</div>
        ) : null}

        {phase === 'pick' ? (
          <div className="pos-cake-type-grid">
            <button type="button" className="pos-cake-type-card" onClick={() => setPhase('counter')}>
              <span className="pos-cake-type-title">Counter cake</span>
              <span className="pos-cake-type-desc">Flavour &amp; weight → price → add to current bill</span>
            </button>
            <button
              type="button"
              className="pos-cake-type-card"
              onClick={() => {
                setDeliveryTime('12:00');
                setPhase('pickup');
              }}
            >
              <span className="pos-cake-type-title">Pickup cake</span>
              <span className="pos-cake-type-desc">Customer, pickup date &amp; time, advance, kitchen copy</span>
            </button>
            <button
              type="button"
              className="pos-cake-type-card"
              onClick={() => {
                setDeliveryTime('18:00');
                setPhase('delivery');
              }}
            >
              <span className="pos-cake-type-title">Delivery</span>
              <span className="pos-cake-type-desc">Address, date &amp; time, advance</span>
            </button>
          </div>
        ) : (
          <>
            <button type="button" className="pos-cake-back" onClick={() => setPhase('pick')}>
              ← Back
            </button>

            <div className="pos-cake-fields">
              <label className="pos-cake-field">
                Flavour
                <select value={flavourId} onChange={onFlavourChange} required>
                  <option value="">Select…</option>
                  {flavours.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="pos-cake-field">
                Weight (kg)
                <select value={weight} onChange={(e) => setWeight(parseFloat(e.target.value))}>
                  {WEIGHT_OPTIONS.map((w) => (
                    <option key={w} value={w}>
                      {w} kg
                    </option>
                  ))}
                </select>
              </label>

              <label className="pos-cake-field">
                Price (₹){' '}
                <span className="pos-cake-hint">auto when configured for this store</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 650"
                />
              </label>
            </div>

            {phase === 'counter' ? (
              <div className="pos-cake-actions">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={handleAddCounter}>
                  Add to cart ({formatInr(parseFloat(price) || 0)})
                </button>
              </div>
            ) : null}

            {(phase === 'pickup' || phase === 'delivery') && (
              <>
                <label className="pos-cake-field">
                  Reference image (optional)
                  <input type="file" accept="image/jpeg,image/png,image/gif" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                </label>

                <div className="pos-cake-row2">
                  <label className="pos-cake-field">
                    Customer name
                    <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
                  </label>
                  <label className="pos-cake-field">
                    Phone
                    <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} required />
                  </label>
                </div>

                {phase === 'delivery' ? (
                  <label className="pos-cake-field">
                    Delivery address
                    <textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
                  </label>
                ) : null}

                <div className="pos-cake-row2">
                  <label className="pos-cake-field">
                    {phase === 'pickup' ? 'Pickup date' : 'Delivery date'}
                    <input
                      type="date"
                      value={deliveryDate}
                      min={serverDate || undefined}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      required
                    />
                  </label>
                  <label className="pos-cake-field">
                    {phase === 'pickup' ? 'Pickup time' : 'Delivery time'}
                    <input
                      type="time"
                      value={deliveryTime}
                      onChange={(e) => setDeliveryTime(e.target.value)}
                      required
                    />
                  </label>
                </div>

                <div className="pos-cake-row2">
                  <label className="pos-cake-field">
                    Advance (₹)
                    <input type="number" min="0" step="1" value={advance} onChange={(e) => setAdvance(e.target.value)} />
                  </label>
                  <label className="pos-cake-field">
                    Advance payment type
                    <select value={advanceMode} onChange={(e) => setAdvanceMode(e.target.value)}>
                      {ADVANCE_PAYMENT_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="pos-cake-due-hint">
                  Balance due after advance:{' '}
                  <strong>{formatInr(Math.max(0, (parseFloat(price) || 0) - (parseFloat(advance) || 0)))}</strong>
                </p>

                <div className="pos-cake-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => submitOrder(phase)}
                  >
                    {busy ? 'Saving…' : 'Create order & print kitchen copy'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Tab content: POS cake orders list + manage */
export function PosCakeOrdersTab({ storeId, formatInr, serverDate, orderCategory = 'Store-1', onNewCakeOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manage, setManage] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMode, setPayMode] = useState('Cash');
  const [busy, setBusy] = useState(false);
  const [listDate, setListDate] = useState('');
  const [dateFilterInited, setDateFilterInited] = useState(false);

  const load = useCallback(async () => {
    const sid = parseInt(storeId, 10);
    if (!sid) {
      setOrders([]);
      return;
    }
    setLoading(true);
    try {
      const today = serverDate || new Date().toISOString().slice(0, 10);
      const from = new Date(`${today}T12:00:00`);
      from.setDate(from.getDate() - 7);
      const to = new Date(`${today}T12:00:00`);
      to.setDate(to.getDate() + 30);
      const { data } = await api.get('/orders', {
        params: {
          posCake: '1',
          posStoreId: sid,
          category: orderCategory,
          startDate: from.toISOString().slice(0, 10),
          endDate: to.toISOString().slice(0, 10)
        }
      });
      setOrders(data.orders || []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [storeId, serverDate, orderCategory]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dateFilterInited && serverDate) {
      setListDate(serverDate);
      setDateFilterInited(true);
    }
  }, [serverDate, dateFilterInited]);

  const visibleOrders = useMemo(() => {
    if (!listDate) return orders;
    return orders.filter((o) => String(o.delivery_date || '').slice(0, 10) === listDate);
  }, [orders, listDate]);

  const openManage = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}`);
      setManage(data.order);
      setPayAmount(
        String(Math.max(0, parseFloat(data.order.due_amount || data.order.total_amount || 0) || 0))
      );
      setPayMode('Cash');
    } catch {
      setManage(null);
    }
  };

  const submitPayment = async () => {
    if (!manage?.id) return;
    const amt = parseFloat(payAmount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    setBusy(true);
    try {
      await api.post(`/pos/cake-orders/${manage.id}/payments`, {
        amount: amt,
        payment_mode: payMode,
        payment_date: serverDate || new Date().toISOString().slice(0, 10),
        notes: 'POS balance payment'
      });
      await load();
      const { data } = await api.get(`/orders/${manage.id}`);
      setManage(data.order);
      setPayAmount(String(Math.max(0, parseFloat(data.order.due_amount || 0))));
    } catch (e) {
      alert(e.response?.data?.error || 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  const markDone = async (status) => {
    if (!manage?.id) return;
    setBusy(true);
    try {
      await api.patch(`/orders/${manage.id}/status`, { status });
      await load();
      setManage(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const sid = parseInt(storeId, 10);
  if (!sid) {
    return <p className="pos-cake-tab-hint">Select an outlet above to see cake orders.</p>;
  }

  return (
    <div className="pos-cake-tab cc-card">
      <div className="pos-cake-tab-head">
        <h2 className="pos-sales-reports-title">Cake orders (pickup / delivery)</h2>
        <div className="pos-cake-tab-head-actions">
          {typeof onNewCakeOrder === 'function' ? (
            <button type="button" className="btn btn-primary" onClick={onNewCakeOrder}>
              New cake order
            </button>
          ) : null}
          <button type="button" className="btn btn-secondary pos-cake-refresh" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      <div className="pos-cake-date-filter">
        <label>
          Pickup / delivery date
          <input
            type="date"
            value={listDate}
            onChange={(e) => setListDate(e.target.value)}
          />
        </label>
        {listDate ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setListDate('')}>
            Show all dates
          </button>
        ) : (
          <span className="pos-cake-date-filter-hint">Showing all ({orders.length})</span>
        )}
        {listDate ? (
          <span className="pos-cake-date-filter-hint">
            {formatDateDisplay(listDate)} · {visibleOrders.length} order{visibleOrders.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      {loading ? <p className="pos-cake-tab-loading">Loading…</p> : null}
      <div className="pos-table-wrap">
        <table className="pos-table pos-table--sales">
          <thead>
            <tr>
              <th>Order</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Mode</th>
              <th>Due</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleOrders.map((o) => (
              <tr key={o.id} className={String(o.status || '').toLowerCase() === 'cancelled' ? 'pos-order-row--cancelled' : ''}>
                <td>{o.order_number}</td>
                <td>
                  {formatDateDisplay(o.delivery_date)}
                  {o.delivery_time ? ` · ${o.delivery_time}` : ''}
                </td>
                <td>{o.customer_name}</td>
                <td>{o.pos_cake_mode}</td>
                <td>{formatInr(o.due_amount ?? 0)}</td>
                <td>{o.status}</td>
                <td>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => openManage(o)}>
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {manage ? (
        <div className="pos-cake-manage-overlay" onClick={() => !busy && setManage(null)}>
          <div className="pos-cake-manage pos-cake-modal-shell" onClick={(e) => e.stopPropagation()}>
            <h3 className="pos-cake-modal-title">{manage.order_number}</h3>
            <p className="pos-cake-detail-meta">
              {manage.customer_name} · {manage.customer_phone}
              {manage.delivery_date
                ? ` · ${formatDateDisplay(manage.delivery_date)}${manage.delivery_time ? ` ${manage.delivery_time}` : ''}`
                : ''}
            </p>
            <p className="pos-cake-detail-pay">
              Total {formatInr(manage.total_amount)} · Paid {formatInr(manage.paid_amount)} ·{' '}
              <strong>Due {formatInr(manage.due_amount)}</strong>
            </p>
            {manage.items?.[0]?.files?.length ? (
              <div className="pos-cake-manage-img">
                {manage.items[0].files
                  .filter((f) => f.file_type === 'cake_image')
                  .map((f) => (
                    <img
                      key={f.id}
                      src={f.file_path?.startsWith('http') ? f.file_path : `${window.location.origin}${f.file_path}`}
                      alt="Reference"
                    />
                  ))}
              </div>
            ) : null}

            <div className="pos-cake-pay-row">
              <label>
                Amount
                <input type="number" min="0" step="1" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
              </label>
              <label>
                Payment type
                <select value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                  {ADVANCE_PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={submitPayment}>
                Record payment
              </button>
            </div>

            <div className="pos-cake-done-actions">
              {manage.pos_cake_mode === 'pickup' ? (
                <button type="button" className="btn btn-primary" disabled={busy || manage.due_amount > 0.01} onClick={() => markDone('Delivered')}>
                  Picked up (mark complete)
                </button>
              ) : (
                <button type="button" className="btn btn-primary" disabled={busy || manage.due_amount > 0.01} onClick={() => markDone('Delivered')}>
                  Delivered
                </button>
              )}
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setManage(null)}>
                Close
              </button>
            </div>
            <p className="pos-cake-done-hint">Complete payment first when there is a balance due.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
